/**
 * @module playerManager
 */
import errors from "../../../web/errors";
import { t } from "web-utils-strings";
import { playerListeners, emit, isEventSupported } from "../../../web/manager/player/listeners";
import heartbeat from "../../../web/manager/player/mediaListsHeartbeat";
import { initIntervalPlayerTime, stopIntervalPlayerTime, initListeners } from "./ps4PlayerListeners";
import { playerErrorCodes } from "../../../web/manager/player/webPlayerErrors";
import {
  tracksInitialize,
  getAvailableSubtitleTracks,
  getCurrentSubtitleTrack,
  setSubtitleTrack,
  getAvailableAudioTracks,
  getCurrentAudioTrack,
  setAudioTrack
} from "./tracks";
import { getPlaybackTokenRequestBody } from "../../../utils/player";
import { webmafAction, resetCallbacks } from "../webmafActions";
import states from "../../../web/manager/player/states";
import { youbora } from "./youbora";
import { sessionId } from "../../../web/store";

const youboraPlugin = "webmaf";
let paused = false;

/**
 * Type of video
 */
const sourceTypes = {
  SMOOTH_STREAMING: 2
};

function canStartPlayback(refCanStartPlayback) {
  return refCanStartPlayback !== null;
}

/**
 * Depending on videoType, requests a playback token for protected content and sets it
 * to the player.
 * @param {Object} config - Configuration information from the video to play.
 */
async function getDrmCustomData(config) {
  if (config.videoType === "movie" || config.videoType === "series") {
    const requestBody = await getPlaybackTokenRequestBody.call(this, config);
    try {
      return await this.authenticationManager.getPlaybackToken(requestBody);
    } catch (e) {
      const RequestError = new Error();
      RequestError.message = e.message;
      RequestError.code = e.code;
      console.log(RequestError);
      this.handleErrorResponse(e);
      return "";
    }
  }
}

/**
 * Checks if a manifest is received to initialize PS4 player (opening content, checking if
 * it is a protected content, setting display area and preparing it asynchronously).
 * @param {Object} config - Configuration information from the video to play.
 */
async function openContent(config) {
  if (config.manifest) {
    try {
      const customData = await getDrmCustomData.call(this, config);
      if (canStartPlayback(config.refCanStartPlayback)) {
        webmafAction("setFixVideoRepresentations", {
          maxBitrate: this.maxBitrate
        });
        webmafAction("setVideoPortalSize", {
          ltx: -1.0,
          lty: 1.0,
          rbx: 1.0,
          rby: -1.0
        });
        webmafAction("load", {
          contentUri: config.manifest,
          licenseUri: this.drmLicenseServer,
          customData,
          sourceType: sourceTypes.SMOOTH_STREAMING
        });
        webmafAction("play", {}, () => {
          configSuccess.call(this, config["playbackTime"]);
          paused = false;
        });
      }
    } catch (e) {
      const RequestError = new Error();
      RequestError.message = e.message;
      RequestError.code = e.code;
      this.handleErrorResponse(e);
      throw RequestError;
    }
  } else {
    throw new Error(t(errors.player.MISSING_REQUIRED_MANIFEST));
  }
}

function configSuccess(playbackTime) {
  if (playbackTime && playbackTime !== 0) {
    this.seek(playbackTime * 1000);
  } else {
    initIntervalPlayerTime();
  }
}

/**
 * Sends init event for playback analytics
 * @param {Object} config - Configuration information
 */
async function sendPlaybackAnalyticsInitEvent(config) {
  await youbora.setOptions.call(this, config);
  youbora.sendEvent(youbora.events.init);
}

/**
 * Player (PS4) mixin methods for the SDK.
 *
 * @param sdk
 * @returns {Object} The object with PS4 player functionalities.
 */
export default {
  sendError: true,

  isDisplayingVideo: false,

  isNewEpisode: false,
  /**
   * Initializes PS4 player with a given configuration.
   * @param {Object} config - Configuration information from the video to play.
   * @param {string} config.manifest - Manifest URL for media content to play.
   * @param {string} config.videoType - Content type: "series" | "movie"| "trailer".
   * @param {Object} config.refCanStartPlayback - Indicates if player can be loaded.
   */
  async setup(config) {
    if (canStartPlayback(config.refCanStartPlayback)) {
      try {
        emit("init");
        this.isNewEpisode = config.playbackAnalytics.newEpisode;
        if (this.isNewEpisode) {
          await sendPlaybackAnalyticsInitEvent.call(this, config);
        }
        initListeners.call(this);
        await openContent.call(this, config);
        tracksInitialize.call(this, config);
        config.videoType !== "trailer" &&
          canStartPlayback(config.refCanStartPlayback) &&
          !config.isLive &&
          heartbeat.initialize({ ...config, heartbeatPeriod: this.heartbeatPeriod }, this, this.isNewEpisode);
      } catch (e) {
        const RequestError = new Error();
        RequestError.message = e.message;
        RequestError.code = e.code;
        this.handleErrorResponse(e);
        throw RequestError;
      }
    }
  },

  pause() {
    if (!paused) {
      emit("pause");
      webmafAction("pause");
      stopIntervalPlayerTime();
      emit("state", states.PAUSED);
      paused = true;
    }
  },

  play() {
    emit("play");
    webmafAction("play");
    paused = false;
  },

  stop() {
    if (!this.isNewEpisode && !this.isDisplayingVideo) {
      youbora.sendEvent(youbora.events.stop);
    }
    emit("willStop");
    webmafAction("stop");
    stopIntervalPlayerTime();
    resetCallbacks();
    emit("stop");
    sessionId.reset();
  },

  /**
   * Jumps to an absolute time in media.
   * @param {number} position  - Miliseconds to jump to. Must be a positive value
   * @param {function} success - Callback method to invoke when the call is successful
   * @param {function} error   - Callback method to invoke when an error occurs
   */
  seek(position, success = null, error = null) {
    try {
      const playTime = Math.round(position / 1000);
      emit("bufferStart");
      webmafAction(
        "setPlayTime",
        {
          playTime
        },
        () => {
          typeof success === "function" && success();
          emit("seek");
          this.play();
        }
      );
    } catch (e) {
      typeof error === "function" && error(e);
    }
  },

  getCurrentTime() {
    return videometrics.elapsed || 0;
  },

  getDuration() {
    return videometrics.duration || 0;
  },

  on(eventName, callback) {
    if (isEventSupported(eventName)) {
      playerListeners[eventName].push(callback);
    }
  },
  off(eventName, listener) {
    if (isEventSupported(eventName)) {
      const listenerIndex = playerListeners[eventName].findIndex(func => listener === func);
      listenerIndex !== -1 && playerListeners[eventName].splice(listenerIndex, 1);
    }
  },
  /**
   * Removes player instance.
   */
  destroy() {
    this.sendError = true;
    this.isNewEpisode = false;
    this.isDisplayingVideo = false;
    emit("destroy");
  },

  error() {
    throw new Error("Couldn't initialize player with given data");
  },

  getCurrentBitrate() {
    return `${videometrics.currentBitrate}`;
  },

  getCurrentBandwidth() {
    return (videometrics && `${videometrics.currentBitrate}`) || "";
  },

  getAvailableSubtitleTracks,
  getCurrentSubtitleTrack,
  setSubtitleTrack,
  getAvailableAudioTracks,
  getCurrentAudioTrack,
  setAudioTrack,
  playerErrorCodes,
  states,
  // TODO Create an "analytics" namespace here that contains the 4 items below
  initPlaybackAnalytics() {
    youbora.loadYouboraLib(this.playbackAnalytics.libSrc, () => {
      youbora.initialize.call(this, youboraPlugin);
    });
  },

  startPlaybackAnalytics(config) {
    return new Promise(async resolve => {
      await sendPlaybackAnalyticsInitEvent.call(this, config);
      resolve();
    });
  },

  sendPlaybackAnalyticsEvent(event, data) {
    event === youbora.events.error && (this.sendError = false);
    youbora.sendEvent(event, data.code, data.name);
  },
  analyticsEvents: youbora.events
};
